import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Upload, Trash2, Image, Loader2, CheckCircle, Lock, Unlock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { configGeneralAPI } from '../api';
import { toast } from '../hooks/use-toast';

const ConfiguracionLogo = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [logoInfo, setLogoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploadEnabled, setUploadEnabled] = useState(true);
  const [savingSettings, setSavingSettings] = useState(false);

  useEffect(() => {
    loadLogo();
  }, []);

  const loadLogo = async () => {
    try {
      setLoading(true);
      const res = await configGeneralAPI.getLogo();
      setLogoInfo(res.data);
      setUploadEnabled(res.data.upload_enabled !== false);
      if (res.data.configured && res.data.url) {
        setPreview(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      }
    } catch (error) {
      console.error('Error loading logo:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleUpload = async (enabled) => {
    try {
      setSavingSettings(true);
      await configGeneralAPI.setLogoUploadEnabled(enabled);
      setUploadEnabled(enabled);
      toast({ 
        title: enabled ? "Subida habilitada" : "Subida deshabilitada",
        description: enabled 
          ? "Los usuarios pueden subir nuevos logos" 
          : "La subida de logos está bloqueada"
      });
    } catch (error) {
      toast({ title: "Error", description: "No se pudo cambiar la configuración", variant: "destructive" });
    } finally {
      setSavingSettings(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tipo
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast({ 
        title: "Tipo no válido", 
        description: "Use imágenes PNG, JPG, SVG o WebP", 
        variant: "destructive" 
      });
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({ 
        title: "Archivo muy grande", 
        description: "El tamaño máximo es 5MB", 
        variant: "destructive" 
      });
      return;
    }

    // Mostrar preview
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(file);

    // Subir archivo
    uploadFile(file);
  };

  const uploadFile = async (file) => {
    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('file', file);

      const res = await configGeneralAPI.uploadLogo(formData);
      
      toast({ title: "Logo actualizado", description: "El logo se ha guardado correctamente" });
      setLogoInfo(res.data);
      setPreview(`${process.env.REACT_APP_BACKEND_URL}${res.data.url}`);
      
      // Forzar recarga del logo en el header
      window.dispatchEvent(new CustomEvent('logo-updated'));
      
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({ 
        title: "Error", 
        description: error.response?.data?.detail || "No se pudo subir el logo", 
        variant: "destructive" 
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar el logo actual?')) return;

    try {
      await configGeneralAPI.deleteLogo();
      toast({ title: "Logo eliminado" });
      setLogoInfo({ configured: false });
      setPreview(null);
      
      // Forzar recarga del logo en el header
      window.dispatchEvent(new CustomEvent('logo-updated'));
      
    } catch (error) {
      toast({ title: "Error", description: "No se pudo eliminar", variant: "destructive" });
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <Button variant="ghost" onClick={() => navigate('/configuracion')} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" /> Volver a Configuración
        </Button>
        <h1 className="text-2xl font-bold text-gray-900">Logo de la Aplicación</h1>
        <p className="text-gray-600 mt-1">Personaliza el logo que se muestra en la barra superior</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* Control de permisos */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                {uploadEnabled ? <Unlock className="w-5 h-5 text-green-600" /> : <Lock className="w-5 h-5 text-red-600" />}
                Control de Acceso
              </CardTitle>
              <CardDescription>
                Habilita o deshabilita la posibilidad de cambiar el logo
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label className="font-medium">Permitir subir/cambiar logo</Label>
                  <p className="text-sm text-gray-500 mt-1">
                    {uploadEnabled 
                      ? "Los administradores pueden subir nuevos logos" 
                      : "La subida de logos está bloqueada"}
                  </p>
                </div>
                <Switch 
                  checked={uploadEnabled}
                  onCheckedChange={handleToggleUpload}
                  disabled={savingSettings}
                />
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Área de subida */}
          <Card className={!uploadEnabled ? 'opacity-60' : ''}>
            <CardHeader>
              <CardTitle className="text-lg">Subir Logo</CardTitle>
              <CardDescription>
                Formatos: PNG, JPG, SVG, WebP. Tamaño máximo: 5MB.
                Se recomienda un logo con fondo transparente.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/svg+xml,image/webp"
                onChange={handleFileSelect}
                className="hidden"
                disabled={!uploadEnabled}
              />
              
              <div 
                onClick={() => uploadEnabled && !uploading && fileInputRef.current?.click()}
                className={`
                  border-2 border-dashed rounded-lg p-8 text-center
                  transition-colors duration-200
                  ${!uploadEnabled 
                    ? 'border-gray-300 bg-gray-100 cursor-not-allowed' 
                    : uploading 
                      ? 'border-gray-300 bg-gray-50 cursor-wait' 
                      : 'border-blue-300 hover:border-blue-500 hover:bg-blue-50 cursor-pointer'}
                `}
              >
                {!uploadEnabled ? (
                  <div className="flex flex-col items-center">
                    <Lock className="w-12 h-12 text-gray-400 mb-3" />
                    <p className="text-gray-500 font-medium">Subida deshabilitada</p>
                    <p className="text-gray-400 text-sm mt-1">Activa el control de acceso para subir logos</p>
                  </div>
                ) : uploading ? (
                  <div className="flex flex-col items-center">
                    <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-3" />
                    <p className="text-gray-600">Subiendo logo...</p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <Upload className="w-12 h-12 text-blue-500 mb-3" />
                    <p className="text-gray-700 font-medium">Haz clic para seleccionar</p>
                    <p className="text-gray-500 text-sm mt-1">o arrastra el archivo aquí</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Vista previa */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vista Previa</CardTitle>
              <CardDescription>
                Así se verá el logo en la barra superior
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Simulación de la barra */}
              <div className="bg-blue-700 rounded-lg p-4 mb-4">
                <div className="flex items-center gap-3">
                  {preview ? (
                    <img 
                      src={preview} 
                      alt="Logo preview" 
                      className="h-8 w-auto max-w-[120px] object-contain"
                    />
                  ) : (
                    <div className="w-8 h-8 bg-white rounded flex items-center justify-center">
                      <span className="text-blue-700 font-bold text-lg">L</span>
                    </div>
                  )}
                  {!preview && <span className="text-white text-xl font-bold">LEXDUO</span>}
                </div>
              </div>

              {/* Logo actual */}
              <div className="border rounded-lg p-4 bg-gray-50">
                {preview ? (
                  <div className="flex flex-col items-center">
                    <div className="bg-white border rounded-lg p-4 mb-4 shadow-sm">
                      <img 
                        src={preview} 
                        alt="Logo actual" 
                        className="max-h-32 w-auto object-contain"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm">Logo configurado</span>
                    </div>
                    <Button 
                      variant="destructive" 
                      size="sm" 
                      onClick={handleDelete}
                      className="mt-4"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Eliminar logo
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center py-4 text-gray-500">
                    <Image className="w-12 h-12 mb-2 opacity-50" />
                    <p>No hay logo configurado</p>
                    <p className="text-sm">Se mostrará el logo por defecto</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          </div>
        </div>
      )}

      {/* Recomendaciones */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-lg">Recomendaciones</CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Usa un logo con fondo transparente (PNG) para mejor integración con la barra azul</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>La altura recomendada es de 32-40 píxeles. El logo se ajustará automáticamente</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Si el logo tiene texto, asegúrate de que sea legible sobre fondo azul</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-blue-500">•</span>
              <span>Para logos con colores oscuros, considera usar una versión en blanco o claro</span>
            </li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default ConfiguracionLogo;
